/*
 * HomeFixer360 - Payment Success Page
 * Copyright (c) 2025 Guy T. Dunn Sr. / Idea Guy Brands Incorporated
 * All Rights Reserved.
 */

import React, { useState, useEffect } from 'react';
import { CheckCircle, Loader, Mail, Calendar, Star, Gift } from 'lucide-react';
import { Button } from '../components/ui/button';

const PaymentSuccess = () => {
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const [pollAttempts, setPollAttempts] = useState(0);

  // Get URL parameters
  const getUrlParameter = (name) => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(name);
  };

  const sessionId = getUrlParameter('session_id');
  const transactionId = getUrlParameter('transaction_id');

  // Poll payment status
  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      setPaymentStatus('timeout');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/status/${sessionId}`);
      
      if (!response.ok) {
        throw new Error('Failed to check payment status');
      }

      const data = await response.json();
      
      if (data.payment_status === 'paid') {
        setPaymentStatus('success');
        setPaymentData(data);
        return;
      } else if (data.status === 'expired') {
        setPaymentStatus('expired');
        return;
      }

      // Continue polling if still pending
      setPollAttempts(attempts + 1);
      setTimeout(() => pollPaymentStatus(attempts + 1), 2000);
      
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentStatus('error');
    }
  };

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setPaymentStatus('error');
    }
  }, [sessionId]);

  if (paymentStatus === 'checking') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Loader className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Confirming Your Payment...</h1>
          <p className="text-gray-600 mb-4">
            Please wait while we confirm your payment. This usually takes just a few seconds.
          </p>
          <p className="text-sm text-gray-500">
            Attempt {pollAttempts + 1} of 5
          </p>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'success') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-500 to-blue-500 text-white p-8 text-center">
            <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
            <h1 className="text-3xl font-bold mb-2">🎉 Payment Successful! 🎉</h1>
            <p className="text-green-100 text-lg">Welcome to the HomeFixer360 presale!</p>
          </div>

          {/* Content */}
          <div className="p-8">
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
              <h2 className="text-lg font-bold text-green-800 mb-3">Your Presale Benefits Are Active:</h2>
              <div className="grid md:grid-cols-2 gap-3 text-sm text-green-700">
                <div className="flex items-center space-x-2">
                  <Gift className="w-4 h-4" />
                  <span>50% OFF Setup Fee Applied</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Star className="w-4 h-4" />
                  <span>Beta Access Until Feb 28, 2026</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4" />
                  <span>3 Months Discounted Rates</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4" />
                  <span>60-Day Money Back Guarantee</span>
                </div>
              </div>
            </div>

            {paymentData && (
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h3 className="font-bold text-gray-900 mb-3">Payment Details:</h3>
                <div className="grid md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Amount Paid:</span>
                    <span className="ml-2">${paymentData.amount} {paymentData.currency.toUpperCase()}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Transaction ID:</span>
                    <span className="ml-2 font-mono text-xs">{paymentData.transaction_id}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
              <div className="flex items-center space-x-3 mb-3">
                <Mail className="w-6 h-6 text-blue-600" />
                <h3 className="font-bold text-blue-800">What Happens Next?</h3>
              </div>
              <ul className="text-blue-700 text-sm space-y-2">
                <li>✅ Confirmation email sent to your inbox</li>
                <li>✅ Beta access available now through February 28, 2026</li>
                <li>✅ You're one of only 50 beta testers</li>
                <li>✅ No additional charges until after beta period ends</li>
                <li>✅ Your discounted monthly billing starts March 2026</li>
                <li>✅ Full access to customer support during beta</li>
              </ul>
            </div>

            <div className="text-center">
              <Button
                onClick={() => window.location.href = '/'}
                className="bg-gradient-to-r from-blue-600 to-green-500 text-white px-8 py-3 text-lg font-semibold rounded-lg mr-4"
              >
                Return to Homepage
              </Button>
              
              <Button
                variant="outline"
                onClick={() => window.location.href = '/partner-program'}
                className="px-8 py-3 text-lg font-semibold rounded-lg"
              >
                Partner Program
              </Button>
            </div>

            <p className="text-center text-xs text-gray-500 mt-6">
              Questions? Contact us at gtdunn@ideaguybrands.com or call (833) 962-2742
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'expired' || paymentStatus === 'timeout' || paymentStatus === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Payment Issue</h1>
          <p className="text-gray-600 mb-6">
            {paymentStatus === 'expired' 
              ? 'Your payment session has expired. Please try again.'
              : paymentStatus === 'timeout'
              ? 'We couldn\'t confirm your payment status. Please check your email or contact support.'
              : 'There was an issue processing your payment. Please try again or contact support.'}
          </p>
          
          <div className="flex flex-col space-y-3">
            <Button
              onClick={() => window.location.href = '/presale-checkout'}
              className="bg-blue-600 text-white px-6 py-3"
            >
              Try Again
            </Button>
            <Button
              variant="outline"
              onClick={() => window.location.href = '/'}
            >
              Return Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default PaymentSuccess;